import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { AgentAuth, AgentAuthContext } from './AgentAuthContext';

// Flag-OFF (pre-#1994) behaviour: the agent console reuses the customer's Visma Online OIDC session
// (react-oidc-context). Sign-in / redirect is still driven by useAuthSetup in the shared
// AuthBootstrap; this adapter only surfaces that session through the AgentAuth seam so the console
// is provider-agnostic. The token getter mirrors the original useAccessToken: ref-backed so its
// identity is stable across hourly silent renews while always returning the current token.
export const OidcAgentAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const tokenRef = useRef<string | null>(auth.user?.access_token ?? null);

    useEffect(() => {
        tokenRef.current = auth.user?.access_token ?? null;
    }, [auth.user?.access_token]);

    const getAccessToken = useCallback(() => tokenRef.current, []);

    // In this flag-off mode the session IS the customer's shared Visma Online session, so this must NOT
    // force a renew (signinSilent) — that would drive silent sign-ins on the customer's session off an
    // agent hub reconnect. react-oidc-context's own automaticSilentRenew keeps it fresh; we just hand
    // back the current token. The agent-specific stale-token-on-reconnect fix lives in the Entra
    // provider, which owns a separate agent session it can safely renew.
    const getFreshAccessToken = useCallback((): Promise<string | null> => Promise.resolve(tokenRef.current), []);

    const value = useMemo<AgentAuth>(
        () => ({
            getAccessToken,
            getFreshAccessToken,
            // No Aia-Oidc-Provider header / ?oidcprovider override is emitted for this value: the
            // backend default is already vismaonline, so flag-off traffic stays byte-identical to
            // the pre-#1994 behaviour.
            oidcProvider: 'vismaonline',
            isLoading: auth.isLoading,
            isAuthenticated: auth.isAuthenticated,
        }),
        [getAccessToken, getFreshAccessToken, auth.isLoading, auth.isAuthenticated],
    );

    return <AgentAuthContext.Provider value={value}>{children}</AgentAuthContext.Provider>;
};
